import { APP_BADGE_MAX, appBadgeCount } from '@garage/shared';
import { useEffect } from 'react';

/**
 * Pastille chiffrée sur l'icône de l'application installée (Badging API, 03/10/2026) : iPhone (iOS
 * 16.4+, application sur l'écran d'accueil, notifications acceptées), Chrome et Edge installés sur
 * ordinateur. Ailleurs (Android, navigateur non installé) la fonction est absente ou sans effet :
 * détection de la fonction, jamais d'erreur.
 */
type BadgeNavigator = {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

function badgeNavigator(): BadgeNavigator | null {
  return typeof navigator === 'undefined' ? null : (navigator as unknown as BadgeNavigator);
}

/** Affiche `count` (borné à `APP_BADGE_MAX`) sur l'icône ; 0 ou moins : efface la pastille. */
export function updateAppBadge(count: number): void {
  const nav = badgeNavigator();
  if (!nav) return;
  const n = Number.isFinite(count) ? Math.min(APP_BADGE_MAX, Math.max(0, Math.trunc(count))) : 0;
  try {
    if (n === 0) {
      if (typeof nav.clearAppBadge === 'function') void Promise.resolve(nav.clearAppBadge()).catch(() => {});
      return;
    }
    if (typeof nav.setAppBadge === 'function') void Promise.resolve(nav.setAppBadge(n)).catch(() => {});
  } catch {
    // Fonction présente mais refusée (permission, plateforme) : sans conséquence.
  }
}

/** Efface la pastille (déconnexion, démonstration, fin de session). */
export function clearAppBadge(): void {
  updateAppBadge(0);
}

/**
 * Synchronise la pastille avec les compteurs du menu (non-lus + décisions à appliquer de ce membre dans
 * le garage actif ; mêmes requêtes, aucun appel de plus). Mise à jour à chaque rafraîchissement, une fois
 * les deux compteurs chargés ; effacée à 0, si `active` est faux (sans droit, démonstration, session
 * support) et quand l'espace garage se ferme (déconnexion).
 */
export function useAppBadgeSync({ active, unread, decisions }: { active: boolean; unread?: number; decisions?: number }): void {
  const count = active && unread !== undefined && decisions !== undefined ? appBadgeCount(unread, decisions) : null;
  useEffect(() => {
    if (!active) clearAppBadge();
    else if (count !== null) updateAppBadge(count);
  }, [active, count]);
  useEffect(() => () => clearAppBadge(), []);
}
